import { useMutation, useQueryClient } from "@tanstack/react-query";
import { gateway } from "../../../../gateway";
import { useAppDispatch } from "../../../../redux/hooks/redux";
import { setCompanies, updateActiveCompany } from "../../../../redux/features/userStateSlice";
import { getSelfCompaniesAction } from "../../../../middlewares/actions/companies-actions";

type UploadCompanyAvatarParams = {
  file: File;
};

type UploadCompanyAvatarResponse = {
  id?: number | string;
  avatar?: string | null;
  logoUrl?: string | null;
  avatarUrl?: string | null;
  imageUrl?: string | null;
};

type UploadCompanyAvatarApiResponse = {
  status?: number;
  message?: string;
  data?: UploadCompanyAvatarResponse;
};

const COMPANY_AVATAR_UPLOAD_ENDPOINT = "/companies/self";

async function uploadCompanyAvatar({ file }: UploadCompanyAvatarParams): Promise<UploadCompanyAvatarResponse> {
  const formData = new FormData();
  formData.append("avatar", file, file.name);

  const response = await gateway.patch<UploadCompanyAvatarApiResponse>(COMPANY_AVATAR_UPLOAD_ENDPOINT, formData);

  return response.data?.data ?? (response.data as UploadCompanyAvatarResponse);
}

export const useUploadCompanyAvatar = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadCompanyAvatar,
    onSuccess: async (company) => {
      dispatch(updateActiveCompany(company));

      if (company?.id !== undefined && company?.id !== null) {
        queryClient.setQueryData(["company-info", company.id], (current: any) => ({
          ...(current ?? {}),
          ...company,
        }));
        queryClient.invalidateQueries({ queryKey: ["company-info", company.id] });
      }

      await queryClient.invalidateQueries({ queryKey: ["companies-self"] });

      const companiesResponse = await getSelfCompaniesAction();
      const companies = companiesResponse?.data ?? companiesResponse?.companies ?? companiesResponse ?? [];
      const companiesWithUploadedAvatar = companies.map((membership: any) => {
        if (String(membership.companyId) !== String(company?.id)) return membership;

        return {
          ...membership,
          company: {
            ...membership.company,
            ...company,
          },
        };
      });

      dispatch(setCompanies(companiesWithUploadedAvatar));
    },
  });
};
